import { Box, useMediaQuery } from '@chakra-ui/react'
import type { DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import moment, { type Moment } from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { AppointmentDto } from '~/clinician-api/models'
import { CalendarEventCard } from './calendar-event-card'
import { FullSizeHeader } from './full-size-header'
import { CalendarHoverPill } from './full-size-hover'

export enum CalendarView {
  TimeGridWeek = 'timeGridWeek',
  DayGridMonth = 'dayGridMonth'
}

interface FullSizeCalendarProps {
  navigationDate: Moment
  selectedView: CalendarView
  onCalendarNavigate: (date: Moment) => void
  onViewChange: (view: CalendarView) => void
  onDateClick: (date: Moment) => void
  onEventClick: (event: AppointmentDto) => void
  calendarRef: React.RefObject<FullCalendar>
  scheduleEvents: EventInput[]
  handleDatesSet: (info: DatesSetArg) => void
  showSidebarMenuButton?: boolean
  onSidebarMenuClick?: () => void
}

export function useIsTablet(): boolean {
  const [isTablet] = useMediaQuery('(min-width: 768px) and (max-width: 1280px)')
  return isTablet
}

export function useIsMobile(): boolean {
  const [isMobile] = useMediaQuery('(max-width: 767px)')
  return isMobile
}

export const FullSizeCalendar: React.FC<FullSizeCalendarProps> = ({
  navigationDate = moment(),
  selectedView,
  onCalendarNavigate,
  onViewChange,
  onDateClick,
  onEventClick,
  calendarRef,
  scheduleEvents,
  handleDatesSet,
  showSidebarMenuButton = false,
  onSidebarMenuClick
}) => {
  const [internalView, setInternalView] = useState<CalendarView>(selectedView)
  const isTablet = useIsTablet()
  const isMobile = useIsMobile()
  const btnRef = useRef(null)

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const calendarDOMRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setInternalView(selectedView)
  }, [selectedView])

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi()
    if (calendarApi && internalView) {
      calendarApi.changeView(internalView)
    }
  }, [internalView, calendarRef])

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi()
    calendarApi?.gotoDate(navigationDate.toDate())
  }, [navigationDate, calendarRef])

  const todayButtonClick = useCallback(() => {
    onCalendarNavigate(moment())
  }, [onCalendarNavigate])

  const previousButtonClick = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi()
    calendarApi?.prev()
    const date = moment(calendarApi?.view.currentStart)
    onCalendarNavigate(date)
  }, [onCalendarNavigate, calendarRef])

  const nextButtonClick = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi()
    calendarApi?.next()
    const date = moment(calendarApi?.view.currentStart)
    onCalendarNavigate(date)
  }, [onCalendarNavigate, calendarRef])

  const handleViewChange = useCallback(
    (view: CalendarView) => {
      setInternalView(view)
      onViewChange(view)
    },
    [onViewChange]
  )

  const handleDateClick = useCallback(
    (info: DateClickArg) => {
      //hover is a 30 minute time slot, so we need to snap to the nearest 30 minute slot, can be reverted if needed
      const clicked = moment(info.date)
      const minutes = clicked.minutes()
      if (minutes < 30) {
        clicked.minutes(0)
      } else {
        clicked.minutes(30)
      }
      clicked.seconds(0)
      onDateClick(clicked)
    },
    [onDateClick]
  )

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      setSelectedEventId(info.event.id) // highlight the event
      onEventClick({
        id: info.event.id,
        subject: info.event.title,
        start: info.event.startStr,
        end: info.event.endStr,
        description: info.event.extendedProps.description,
        patient: info.event.extendedProps.patient,
        recurrenceTemplate: info.event.extendedProps.recurrenceTemplate,
        setting: info.event.extendedProps.setting,
        serviceType: info.event.extendedProps.serviceType,
        sourceCalendarType: info.event.extendedProps.sourceCalendarType
      } as AppointmentDto)
    },
    [onEventClick]
  )

  //deselect state on outside click
  useEffect(() => {
    const handleClick = () => setSelectedEventId(null)
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <Box
      className={
        internalView === CalendarView.DayGridMonth ? 'calendar-monthly' : ''
      }
      height="100%"
      px={0}
      py={0}
      position="relative"
      sx={{
        '--fc-border-color': '#E4E5E6',
        '& .fc-scrollgrid, & .fc-scrollgrid-section': {
          borderRadius: '16px',
          borderRight: 'none',
          borderBottom: 'none',
          overflow: 'hidden'
        },
        '& .fc-scrollgrid-sync-table': {
          borderRight: 'none',
          borderBottom: 'none'
        },
        '& .fc-scrollgrid-section-footer, & .fc-scrollgrid-section-header': {
          border: 'none'
        },
        '& .fc-weekend-grey': {
          backgroundColor: '#F9F9F9',
          borderBottom: '1px dashed rgba(87, 89, 91, 0.04)'
        },
        '& .fc-day-today': {
          backgroundColor: '#2d54E8',
          color: '#fff',
          borderRadius: '18px'
        },
        '& .fc-timegrid-col.fc-day-today': {
          backgroundColor: 'rgba(45,84,232,0.04)',
          borderRadius: '2px'
        },
        '& .fc-daygrid-day.fc-day-today': {
          backgroundColor: 'rgba(45,84,232,0.04)',
          boxShadow: 'none',
          borderRadius: '2px'
        },
        '& .fc-timegrid-event': {
          marginBottom: '0px'
        },
        '& .fc-daygrid-day.fc-day-today .fc-daygrid-day-top': {
          backgroundColor: '#2d54E8',
          borderRadius: '18px',
          color: '#fff',
          width: '100%',
          padding: '0px 4px',
          fontWeight: 500,
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexDirection: 'column',
          textAlign: 'left',
          boxSizing: 'border-box'
        },
        '& .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': {
          color: 'black',
          borderRadius: '999px',
          display: 'inline-block',
          padding: '4px 8px'
        },
        '& .fc-timegrid-axis.fc-day-today, & .fc-timegrid-all-day.fc-day-today':
          {
            backgroundColor: '#F0F6FF'
          },
        '& .fc-col-header-cell.fc-day-sun, & .fc-col-header-cell.fc-day-sat': {
          backgroundColor: '#F9F9F9',
          color: '#E4E5E6'
        },
        '& .fc-timegrid-now-indicator-arrow': {
          width: '8px',
          height: '8px',
          background: '#E32636',
          border: 'none',
          left: '0',
          right: 'unset',
          marginLeft: '0',
          clipPath: 'polygon(0 0, 100% 50%, 0 100%)'
        },
        '& .fc-timegrid-now-indicator-line': {
          background: '#E32636',
          zIndex: 9
        },
        '& .fc-timegrid-col': {
          position: 'relative',
          overflow: 'visible'
        },
        '& .fc-timegrid-axis': {
          borderRight: '1px solid #E4E5E6',
          backgroundColor: 'transparent',
          position: 'relative',
          zIndex: 1,
          paddingRight: '8px'
        },
        '& .fc-timegrid-slot-label.fc-timegrid-slot-label': {
          borderRight: '1px solid #E4E5E6',
          position: 'relative',
          zIndex: 2,
          '&[data-time$=":15:00"], &[data-time$=":30:00"], &[data-time$=":45:00"]':
            {
              borderTop: 'none',
              borderBottom: 'none'
            },
          '&[data-time$=":00:00"]': {
            borderTop: '1px solid rgba(55, 60, 65, 0.14)'
          }
        },
        '& .fc-timegrid-slot[data-time$=":15:00"], & .fc-timegrid-slot[data-time$=":45:00"]':
          {
            borderTop: '1px solid rgb(255, 255, 255, 0)',
            borderStyle: 'solid',
            borderWidth: '1px 0 0 0'
          },
        '& .fc-timegrid-slot[data-time$=":00:00"]': {
          borderTop: '1px solid rgba(55, 60, 65, 0.14)'
        },
        '& .fc-timegrid-slot[data-time$=":30:00"]': {
          borderTop: '1px dotted rgba(55, 60, 65, 0.14)',
          borderStyle: 'dotted',
          borderWidth: '1px 0 0 0'
        },
        '& .fc-timegrid-axis + .fc-timegrid-slot': {
          borderTop: 'none'
        },
        '& .fc-timegrid-axis + .fc-timegrid-slot[data-time$=":00:00"]': {
          borderTop: '1px solid rgba(55, 60, 65, 0.14)'
        },
        '& .fc-timegrid-axis.fc-scrollgrid-shrink .fc-timegrid-axis-cushion': {
          display: 'none'
        },
        '& .fc-timegrid-axis-cushion, .fc-timegrid-slot-label': {
          fontSize: '10px',
          fontWeight: 400,
          color: '#57595B',
          textAlign: 'right',
          fontVariant: 'normal',
          textTransform: 'none'
        },
        '& .fc-timegrid-slot': {
          backgroundClip: 'padding-box',
          borderRight: 'none',
          borderBottom: 'none',
          height: '20px',
          minHeight: '20px'
        },
        '& .fc .fc-timegrid-slot': {
          height: '32px',
          minHeight: '30px'
        },
        '& .fc-timegrid-divider.fc-cell-shaded': {
          border: 'none',
          borderBottom: 'none',
          background: 'transparent',
          boxShadow: 'none',
          margin: '0',
          padding: '0',
          height: '0',
          minHeight: 'unset'
        },
        '& .fc-col-header-cell': {
          borderBottom: '1px solid #E4E5E6',
          textAlign: 'left',
          paddingLeft: '4px',
          verticalAlign: 'top',
          height: '24px',
          minHeight: '24px',
          lineHeight: 1.45
        },
        '& .fc-col-header-cell:not(.fc-day-sun):not(.fc-day-sat)': {
          color: '#3B3E45'
        },
        '& .fc-timegrid-event-harness .fc-event, & .fc-timegrid-event-harness .fc-timegrid-event, & .fc-daygrid-event-harness .fc-event, & .fc-daygrid-event-harness .fc-daygrid-event':
          {
            background: 'transparent',
            border: 'none',
            boxShadow: 'none'
          }, //monthly view popover styles
        '& .fc-daygrid-more-link': {
          color: '#2563eb',
          fontWeight: 700,
          fontSize: '13px',
          cursor: 'pointer',
          marginTop: '2px',
          display: 'block',
          background: 'none',
          textDecoration: 'none'
        },
        '& .fc-popover': {
          background: '#fff',
          boxShadow:
            '0 8px 32px 0 rgba(37,99,235,0.15), 0 1.5px 4px 0 rgba(60,60,120,0.08)',
          borderRadius: '18px',
          border: '1.5px solid #f2f3f5'
        },
        '& .fc .fc-popover-header': {
          background: '#fff',
          fontWeight: 700,
          fontSize: '15px',
          color: '#3B3E45',
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          padding: '8px 12px',
          borderBottom: '1px solid #f2f3f5'
        },
        '& .fc .fc-popover-header .fc-popover-title': {
          fontWeight: 700,
          fontSize: '10px',
          color: '#3B3E45'
        },
        '& .fc-popover.fc-day-today, & .fc-popover.fc-today-pill': {
          background: '#fff',
          margin: 0,
          padding: 0,
          boxShadow:
            '0 8px 32px 0 rgba(37,99,235,0.15), 0 1.5px 4px 0 rgba(60,60,120,0.08)',
          borderRadius: '18px',
          border: '1.5px solid #f2f3f5'
        },
        '& .fc-more-popover-misc': {
          height: '0px'
        },
        '& .fc-popover.fc-day-today .fc-popover-header': {
          background: '#2d54E8',
          color: '#fff'
        },
        '& .fc-popover.fc-day-today .fc-popover-header .fc-popover-title': {
          color: '#fff'
        },
        ...(internalView === CalendarView.DayGridMonth
          ? {
              '& .fc-col-header-cell': {
                textAlign: 'left',
                paddingLeft: '6px'
              },
              '& .fc-col-header-cell-cushion': {
                fontSize: '10px',
                fontWeight: 430,
                fontStyle: 'normal',
                lineHeight: 1.45,
                color: '#282828',
                textAlign: 'left'
              },
              '& .fc-col-header-cell.fc-day-sun .fc-col-header-cell-cushion, & .fc-col-header-cell.fc-day-sat .fc-col-header-cell-cushion':
                {
                  color: '#A1A1A1'
                },
              '& .fc-daygrid-day-number': {
                float: 'none',
                display: 'inline-block',
                textAlign: 'left',
                marginLeft: '0',
                paddingLeft: '4px',
                position: 'static',
                fontWeight: 400,
                fontSize: '15px',
                color: '#3B3E45'
              },
              '& .fc-daygrid-day-top': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                paddingLeft: '4px'
              },
              '& .fc-today-pill': {
                background: 'none'
              },
              '& .fc-daygrid-day.fc-day-today': {
                backgroundColor: 'transparent'
              }
            }
          : {}),
        ...(isMobile
          ? {
              ...(internalView === CalendarView.DayGridMonth
                ? {
                    '& .fc-timegrid-col.fc-timegrid-axis': {
                      display: 'none'
                    }
                  }
                : {}),
              '& .fc-view-harness': {
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                width: '100vw',
                minWidth: '0',
                height: '100%',
                maxWidth: '100vw'
              },
              '& .fc-daygrid, & .fc-timegrid': {
                minWidth: '650px'
              }
            }
          : {})
      }}
    >
      <FullSizeHeader
        isMobile={isMobile}
        navigationDate={navigationDate}
        selectedView={internalView}
        showSidebarMenuButton={showSidebarMenuButton}
        onSidebarMenuClick={onSidebarMenuClick}
        previousButtonClick={previousButtonClick}
        nextButtonClick={nextButtonClick}
        todayButtonClick={todayButtonClick}
        handleViewChange={handleViewChange}
        btnRef={btnRef}
      />

      <div
        ref={calendarDOMRef}
        style={{
          position: 'relative',
          height: '100%',
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <div
          style={
            isMobile
              ? { overflowX: 'auto', width: '100vw', height: '100%' }
              : { height: '100%' }
          }
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
            initialView={internalView}
            weekends={true}
            headerToolbar={false}
            height="92%"
            nowIndicator={true}
            scrollTime="07:30:00"
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            events={scheduleEvents}
            datesSet={handleDatesSet}
            slotDuration="00:30:00"
            slotLabelInterval="01:00:00"
            allDaySlot={true}
            allDayText="All Day"
            dayMaxEvents={
              internalView === CalendarView.DayGridMonth ? 3 : false
            }
            firstDay={0}
            slotLabelContent={arg => {
              if (isTablet && internalView === CalendarView.TimeGridWeek) {
                return null
              }
              const time = arg.text
              const match = time.match(/(\d+)(:\d+)?\s?(am|pm)?/i)
              if (!match) return time
              const [, hour, minute, ampm] = match
              return (
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-start',
                    width: '100%',
                    textAlign: 'right',
                    fontSize: '10px',
                    fontWeight: 430,
                    fontStyle: 'normal',
                    lineHeight: 1.45,
                    letterSpacing: '0px',
                    color: '#757575',
                    fontVariant: 'normal',
                    textTransform: 'none'
                  }}
                >
                  <span style={{ color: '#757575' }}>
                    {hour}
                    {minute}
                  </span>
                  {ampm && (
                    <span
                      style={{
                        marginLeft: '2px',
                        fontSize: '8px',
                        fontWeight: 430,
                        fontStyle: 'normal',
                        letterSpacing: '0px',
                        color: '#757575',
                        verticalAlign: 'top',
                        marginTop: '2px',
                        textAlign: 'right',
                        lineHeight: 1.45,
                        fontVariant: 'normal',
                        textTransform: 'none'
                      }}
                    >
                      {ampm.toUpperCase()}
                    </span>
                  )}
                </span>
              )
            }}
            eventContent={arg => {
              const event = arg.event
              const isPast = moment(event.end).isBefore(moment())
              const time = moment(event.start).format('h:mm')
              const name = event.title.replace(/^Appointment with /i, '').trim()
              const duration = moment(event.end).diff(
                moment(event.start),
                'minutes'
              )
              const isBlueprint =
                !event.extendedProps.source ||
                event.extendedProps.source === 'blueprint'
              const isSessionOver = isPast
              const isSelected = event.id === selectedEventId
              return (
                <CalendarEventCard
                  event={event}
                  isSelected={isSelected}
                  onSelect={e => {
                    e.stopPropagation()
                    setSelectedEventId(event.id)
                    onEventClick({
                      id: event.id,
                      subject: event.title,
                      start: event.startStr,
                      end: event.endStr,
                      description: event.extendedProps.description,
                      patient: event.extendedProps.patient,
                      recurrenceTemplate:
                        event.extendedProps.recurrenceTemplate,
                      setting: event.extendedProps.setting,
                      serviceType: event.extendedProps.serviceType,
                      sourceCalendarType: event.extendedProps.sourceCalendarType
                    } as AppointmentDto)
                  }}
                  time={time}
                  name={name}
                  duration={duration}
                  isSessionOver={isSessionOver}
                  isMobile={isMobile}
                  isMonthView={internalView === CalendarView.DayGridMonth}
                />
              )
            }}
            dayCellClassNames={arg => {
              const day = arg.date.getDay()
              const classes = []
              if (day === 0 || day === 6) classes.push('fc-weekend-grey')
              if (arg.isToday) classes.push('fc-today-pill')
              return classes
            }}
            dayCellContent={
              internalView === CalendarView.DayGridMonth
                ? arg => {
                    const isToday = arg.isToday
                    const dayNumber = arg.dayNumberText
                    const dayShort = arg.date.toLocaleDateString('en-US', {
                      weekday: 'short'
                    })

                    return (
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 400,
                          color: isToday ? '#fff' : '#3B3E45'
                        }}
                      >
                        {isToday
                          ? isMobile
                            ? `${dayShort} ${dayNumber}`
                            : `Today ${dayNumber}`
                          : dayNumber}
                      </span>
                    )
                  }
                : undefined
            }
            dayHeaderContent={
              internalView === CalendarView.TimeGridWeek
                ? arg => {
                    const dayMap: Record<string, string> = {
                      Tue: 'Tues',
                      Thu: 'Thur'
                    }
                    let day = arg.date.toLocaleDateString('en-US', {
                      weekday: 'short'
                    })
                    day = dayMap[day] || day
                    const date = arg.date.getDate()
                    const isWeekend =
                      arg.date.getDay() === 0 || arg.date.getDay() === 6
                    const isToday = arg.isToday

                    let dayColor = '#282828'
                    if (isToday) {
                      dayColor = '#fff'
                    } else if (isWeekend) {
                      dayColor = '#A1A1A1'
                    }

                    let dateColor = '#757575'
                    if (isToday) {
                      dateColor = '#96A9F3'
                    } else if (isWeekend) {
                      dateColor = '#A1A1A1'
                    }

                    if (isToday) {
                      return isMobile ? (
                        <span
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            fontSize: '10px',
                            fontStyle: 'normal',
                            justifyContent: 'flex-start',
                            width: '100%',
                            color: '#fff',
                            fontWeight: 430,
                            lineHeight: 1.45,
                            textAlign: 'left',
                            paddingLeft: 0
                          }}
                        >
                          <span style={{ marginRight: 4 }}>{day}</span>
                          <span>{date}</span>
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'flex',
                            fontSize: '10px',
                            fontStyle: 'normal',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            width: '100%',
                            color: '#fff',
                            fontWeight: 430,
                            lineHeight: 1.45,
                            textAlign: 'left',
                            paddingLeft: 0
                          }}
                        >
                          <span style={{ marginRight: 4 }}>Today</span>
                          <span style={{ marginRight: 4 }}>{day}</span>
                          <span
                            style={{
                              color: dateColor,
                              fontWeight: 430,
                              fontSize: '10px',
                              fontStyle: 'normal',
                              lineHeight: 1.45
                            }}
                          >
                            {date}
                          </span>
                        </span>
                      )
                    }

                    return (
                      <span
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          width: '100%',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          lineHeight: 1.45,
                          textAlign: 'left',
                          paddingLeft: 0
                        }}
                      >
                        <span
                          style={{
                            color: dayColor,
                            fontWeight: 430,
                            marginRight: 4,
                            fontSize: '10px',
                            fontStyle: 'normal',
                            lineHeight: 1.45
                          }}
                        >
                          {day}
                        </span>
                        <span
                          style={{
                            color: dateColor,
                            fontWeight: 430,
                            fontSize: '10px',
                            fontStyle: 'normal',
                            lineHeight: 1.45
                          }}
                        >
                          {date}
                        </span>
                      </span>
                    )
                  }
                : undefined
            }
          />
        </div>
        {!isTablet && (
          <CalendarHoverPill
            calendarDOMRef={calendarDOMRef}
            scheduleEvents={scheduleEvents}
          />
        )}
      </div>
    </Box>
  )
}
